import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const pages = ["home", "services", "products", "contact"];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (page) => {
    if (page === "about") {
      navigate("/");
      setTimeout(() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      navigate(page === "home" ? "/" : `/${page}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setOpen(false);
  };

  const active = (p) => p === "home" ? location.pathname === "/" : location.pathname === `/${p}`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        :root{
          --blue:#2563eb;--blue2:#3b82f6;
          --black:#080808;--bdd:rgba(255,255,255,.07);
          --f:'Inter',sans-serif;
        }

        .nav{
          position:fixed;top:0;left:0;right:0;z-index:1000;
          font-family:var(--f);
          transition:background .4s ease,border-color .4s ease,backdrop-filter .4s ease;
        }
        .nav.scrolled{
          background:rgba(8,8,8,.9);
          backdrop-filter:blur(20px) saturate(1.5);
          border-bottom:1px solid var(--bdd);
        }

        .nav-in{
          max-width:1900px;margin:0 auto;
          padding:0 2rem;height:100px;
          display:flex;align-items:center;justify-content:space-between;
          opacity:0;transform:translateY(-10px);
          transition:opacity .6s ease,transform .6s cubic-bezier(.16,1,.3,1);
        }
        .nav-in.vis{opacity:1;transform:translateY(0)}

        /* LOGO */
        .nav-logo{
          display:flex;align-items:center;gap:14px;
          cursor:pointer;text-decoration:none;flex-shrink:0;
        }
        .nav-logo-img{
          height:100px;width:auto;object-fit:contain;
          filter:drop-shadow(0 0 10px rgba(37,99,235,.4)) brightness(1.15);
          transition:filter .3s ease,transform .3s ease;
        }
        .nav-logo:hover .nav-logo-img{
          filter:drop-shadow(0 0 16px rgba(37,99,235,.65)) brightness(1.25);
          transform:scale(1.04);
        }
        .nav-logo-fallback{
          font-size:1.15rem;font-weight:800;color:#fff;
          letter-spacing:-.025em;
        }
        .nav-logo-fallback span{color:var(--blue2)}

        /* LINKS */
        .nav-links{display:flex;align-items:center;gap:.15rem;list-style:none;margin:0;padding:0}
        .nav-btn{
          position:relative;background:none;border:none;cursor:pointer;
          padding:.48rem 1.05rem;font-family:var(--f);font-size:.78rem;
          font-weight:600;letter-spacing:.06em;text-transform:uppercase;
          color:rgba(255,255,255,.4);transition:color .2s ease;border-radius:6px;
        }
        .nav-btn::after{
          content:'';position:absolute;bottom:2px;left:50%;
          transform:translateX(-50%) scaleX(0);
          width:16px;height:2px;background:var(--blue2);border-radius:2px;
          transition:transform .25s cubic-bezier(.34,1.56,.64,1);
        }
        .nav-btn:hover,.nav-btn.on{color:#fff}
        .nav-btn:hover::after,.nav-btn.on::after{transform:translateX(-50%) scaleX(1)}

        /* CTA */
        .nav-cta{
          background:var(--blue);border:none;border-radius:7px;color:#fff;
          cursor:pointer;font-family:var(--f);font-size:.78rem;font-weight:700;
          letter-spacing:.05em;text-transform:uppercase;padding:.62rem 1.3rem;
          transition:all .25s ease;white-space:nowrap;
          box-shadow:0 4px 16px rgba(37,99,235,.3);
        }
        .nav-cta:hover{background:#1d4ed8;transform:translateY(-2px);box-shadow:0 8px 24px rgba(37,99,235,.55)}

        /* HAMBURGER */
        .nav-ham{
          display:none;background:rgba(255,255,255,.05);
          border:1px solid var(--bdd);border-radius:8px;
          padding:.52rem;cursor:pointer;color:#fff;
          transition:all .2s ease;align-items:center;justify-content:center;
        }
        .nav-ham:hover{background:rgba(255,255,255,.1)}

        /* MOBILE OVERLAY */
        .mob{
          position:fixed;inset:0;z-index:999;
          background:#080808;
          display:flex;flex-direction:column;
          transform:translateX(100%);
          transition:transform .45s cubic-bezier(.16,1,.3,1);
        }
        .mob.open{transform:translateX(0)}
        .mob-top{
          display:flex;justify-content:space-between;align-items:center;
          padding:1.35rem 1.75rem;border-bottom:1px solid var(--bdd);
        }
        .mob-brand{font-family:var(--f);font-size:1.05rem;font-weight:800;color:#fff;letter-spacing:-.02em}
        .mob-brand span{color:var(--blue2)}
        .mob-close{
          background:rgba(255,255,255,.06);border:1px solid var(--bdd);
          border-radius:8px;color:#fff;cursor:pointer;padding:.52rem;
          display:flex;align-items:center;justify-content:center;transition:all .2s ease;
        }
        .mob-close:hover{background:rgba(255,255,255,.12)}
        .mob-links{flex:1;display:flex;flex-direction:column;justify-content:center;padding:2rem 1.75rem}
        .mob-link{
          background:none;border:none;color:rgba(255,255,255,.3);cursor:pointer;
          font-family:var(--f);font-size:2.6rem;font-weight:900;
          letter-spacing:-.045em;padding:.45rem 0;text-align:left;
          transition:all .22s ease;
          border-bottom:1px solid rgba(255,255,255,.05);
        }
        .mob-link:last-child{border-bottom:none}
        .mob-link:hover{color:#fff;padding-left:1rem}
        .mob-foot{padding:1.75rem;border-top:1px solid var(--bdd)}
        .mob-cta-btn{
          display:block;width:100%;background:var(--blue);border:none;
          border-radius:10px;color:#fff;cursor:pointer;font-family:var(--f);
          font-size:.95rem;font-weight:700;letter-spacing:.04em;
          padding:.95rem;text-transform:uppercase;transition:all .25s ease;
        }
        .mob-cta-btn:hover{background:#1d4ed8}

     @media(max-width:900px){
  .nav-links,.nav-cta{display:none}
  .nav-ham{display:flex}
  .nav-logo-img{height:60px}
}
      `}</style>

      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className={`nav-in ${mounted ? "vis" : ""}`}>

          {/* Logo */}

          <div className="nav-logo" onClick={() => go("home")}>
            <img
              src="/images/123.png"
              alt="WayForward IT"
              className="nav-logo-img"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
            <span className="nav-logo-fallback" style={{ display: "none" }}>
              Way<span>Forward</span> IT
            </span>
          </div>

          {/* Desktop links */}
          <ul className="nav-links">
            {pages.map((p, i) => (
              <li key={p}>
                <button
                  className={`nav-btn ${active(p) ? "on" : ""}`}
                  onClick={() => go(p)}
                  style={{ transitionDelay: `${i * .05}s` }}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              </li>
            ))}
          </ul>

          <button className="nav-cta" onClick={() => go("contact")}>Get Started</button>
          <button className="nav-ham" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu size={21} />
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div className={`mob ${open ? "open" : ""}`}>
        <div className="mob-top">
          <span className="mob-brand">Way<span>Forward</span> IT</span>
          <button className="mob-close" onClick={() => setOpen(false)}><X size={21} /></button>
        </div>
        <div className="mob-links">
          {pages.map((p) => (
            <button key={p} className="mob-link" onClick={() => go(p)}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="mob-foot">
          <button className="mob-cta-btn" onClick={() => go("contact")}>Get Started →</button>
        </div>
      </div>
    </>
  );
}
